import React, { CSSProperties, useEffect, useLayoutEffect } from 'react';
import PortalModalWrapper from '@components/wrapper/PortalModalWrapper';
import { ModalContent, ModalOverlay } from './ModalStyle';

const fadeVariants = {
  visible: { opacity: 1 },
  hidden: { opacity: 0 },
};

// 기존 모달 애니메이션
const modalVariants = {
  enter: {
    opacity: 0,
    y: '15%',
  },
  in: {
    opacity: 1,
    y: 0,
  },
  out: {
    opacity: 1,
    y: '-15%',
  },
};

// 새로운 스케일 애니메이션 (PopupEditor에서 가져온 것)
const scaleModalVariants = {
  enter: {
    opacity: 0,
    scale: 0.8,
    y: 20,
  },
  in: {
    opacity: 1,
    scale: 1,
    y: 0,
  },
  out: {
    opacity: 0,
    scale: 0.8,
    y: 20,
  },
};

const spring = {
  type: 'spring',
  duration: 0.4,
  bounce: 0.3,
  delay: 0.1,
};

const scaleSpring = {
  type: 'spring',
  duration: 0.3,
};

type ModalVariantType = 'default' | 'scale';

type modalWrapperProps = {
  children: JSX.Element[] | JSX.Element | null;
  outsideClick: any;
  key?: string;
  contentStyle?: React.CSSProperties;
  blockZindex?: boolean;
  id?: string;
  style?: CSSProperties;
  variant?: ModalVariantType;
};

function ModalWrapper({
  children,
  key,
  outsideClick,
  contentStyle,
  blockZindex = true,
  id,
  style,
  variant = 'default',
}: modalWrapperProps) {
  //바깥 스크롤 막기
  useLayoutEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  useEffect(() => {
    const el = document.getElementById('portalModal');
    return () => {
      // 중복 모달 = children수가 1개 이상 이면 zindex 유지
      const portalContents =
        document.getElementsByClassName('portalModalContent');
      if (portalContents.length > 1) {
        return;
      } else {
        el!.style.zIndex = '0';
      }
    };
  }, [blockZindex]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        outsideClick();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    // Cleanup function to remove the event listener when the component unmounts
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [outsideClick]);

  // variant에 따른 애니메이션 선택
  const selectedModalVariants =
    variant === 'scale' ? scaleModalVariants : modalVariants;
  const selectedTransition = variant === 'scale' ? scaleSpring : spring;

  return (
    <PortalModalWrapper blockZindex={blockZindex}>
      <ModalOverlay
        id={id}
        className="portalModalContent"
        key={key}
        initial="hidden"
        animate="visible"
        exit="hidden"
        transition={{ duration: 0.1 }}
        variants={fadeVariants}
        style={style}
        onClick={() => {
          if (outsideClick) {
            outsideClick();
          }
        }}
      >
        <ModalContent
          key={key}
          initial="enter"
          animate="in"
          exit="out"
          variants={selectedModalVariants}
          transition={selectedTransition}
          onClick={(e) => e.stopPropagation()}
          style={contentStyle}
        >
          {children}
        </ModalContent>
      </ModalOverlay>
    </PortalModalWrapper>
  );
}

export default React.memo(ModalWrapper);
